import { useState } from 'react';
import { Table, Select, InputNumber, Input, Button, Space, Tag, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, LockOutlined, EditOutlined } from '@ant-design/icons';

function ValueInput({ dataType, value, onChange }) {
  if (dataType === 'decimal') {
    return (
      <InputNumber
        value={value}
        onChange={onChange}
        style={{ width: '100%' }}
        decimalSeparator=","
        step={0.01}
        stringMode
      />
    );
  }
  if (dataType === 'integer') {
    return (
      <InputNumber
        value={value}
        onChange={onChange}
        style={{ width: '100%' }}
        precision={0}
      />
    );
  }
  return <Input value={value} onChange={e => onChange(e.target.value)} />;
}

// props:
//   properties      – [{ fk_property, property_name, unit_name, data_type, value, fk_property_value, inherited? }]
//   allProperties   – [{ id, name, unit_name, data_type }] from /meta/properties
//   onChange        – (newList) => void
//   readOnly?
export default function PropertyEditor({ properties = [], allProperties = [], onChange, readOnly }) {
  const [overriding, setOverriding] = useState({});

  const usedIds = new Set(
    properties.filter(p => !p.inherited || overriding[p.fk_property]).map(p => p.fk_property)
  );

  function addProperty(propertyId) {
    const meta = allProperties.find(p => p.id === propertyId);
    if (!meta) return;
    onChange([...properties, { fk_property: meta.id, property_name: meta.name, unit_name: meta.unit_name, data_type: meta.data_type, value: null }]);
  }

  function updateValue(index, value) {
    const next = [...properties];
    next[index] = { ...next[index], value };
    onChange(next);
  }

  function removeProperty(index) {
    const next = properties.filter((_, i) => i !== index);
    onChange(next);
  }

  function startOverride(prop) {
    setOverriding(prev => ({ ...prev, [prop.fk_property]: true }));
    onChange([...properties, {
      fk_property: prop.fk_property,
      property_name: prop.property_name,
      unit_name: prop.unit_name,
      data_type: prop.data_type,
      value: null,
      overriding: true,
    }]);
  }

  const availableToAdd = allProperties.filter(p => !usedIds.has(p.id));

  const columns = [
    {
      title: 'Eigenschaft',
      dataIndex: 'property_name',
      width: 180,
      render: (name, row) => (
        <Space>
          {name}
          <Tag color="default" style={{ fontSize: 11 }}>{row.unit_name}</Tag>
          {row.inherited && !overriding[row.fk_property] && (
            <Tooltip title="Geerbt — zum Überschreiben klicken">
              <LockOutlined style={{ color: '#aaa' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Wert',
      dataIndex: 'value',
      render: (value, row, index) => {
        if (row.inherited && !overriding[row.fk_property]) {
          return <span style={{ color: '#aaa' }}>{value ?? '—'}</span>;
        }
        if (readOnly) return value ?? '—';
        return (
          <ValueInput
            dataType={row.data_type}
            value={value}
            onChange={v => updateValue(index, v)}
          />
        );
      },
    },
    {
      title: '',
      width: 60,
      render: (_, row, index) => {
        if (readOnly) return null;
        if (row.inherited && !overriding[row.fk_property]) {
          return (
            <Tooltip title="Überschreiben">
              <Button size="small" icon={<EditOutlined />} onClick={() => startOverride(row)} />
            </Tooltip>
          );
        }
        return (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              removeProperty(index);
              if (row.overriding) {
                setOverriding(prev => { const n = { ...prev }; delete n[row.fk_property]; return n; });
              }
            }}
          />
        );
      },
    },
  ];

  return (
    <div>
      <Table
        dataSource={properties}
        columns={columns}
        rowKey={(r, i) => `${r.fk_property}-${i}`}
        pagination={false}
        size="small"
        rowClassName={row => (row.inherited && !overriding[row.fk_property]) ? 'property-inherited' : ''}
        style={{ marginBottom: 8 }}
      />
      {!readOnly && availableToAdd.length > 0 && (
        <Select
          placeholder="Eigenschaft hinzufügen…"
          style={{ width: '100%' }}
          value={null}
          onChange={addProperty}
          options={availableToAdd.map(p => ({
            value: p.id,
            label: `${p.name} (${p.unit_name})`,
          }))}
          showSearch
          filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
          suffixIcon={<PlusOutlined />}
        />
      )}
    </div>
  );
}
